import '../lib/rum';
import '../lib/sentry';

import type { ReactElement, ReactNode } from 'react';
import React, { Suspense } from 'react';
import { config } from '@fortawesome/fontawesome-svg-core';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import { theme } from '@components-shared/src/stitches';
import { DASHBOARD_PATHNAMES, UPLOAD_PATHNAMES } from '@components/LeftNav/helpers';
import { LeftNavState } from '@components/LeftNav/store';
import type { ProtectedRoutes } from '@components/PrivateRouteGuard';
import { NavigationEvents } from '@components/app-dir/NavigationEvents';
import { type SharedProps } from '@components/layout';
import { useUpdateParams } from '@context/params';
import { useIgnoreBenignErrors } from '@hooks/useIgnoreBenignErrors';
import { logError } from '@hooks/useLogErrorNew';
import { useTrackPopState } from '@hooks/usePopState';
import { useSyncRouterHistory } from '@store/router';
import { isClientSide } from '@utils/ssr';

import 'keen-slider/keen-slider.min.css';
import 'tippy.js/dist/svg-arrow.css';
import '../styles/reset.css';
import '../styles/globals.css';
import '../utils/patches';
import 'react-circular-progressbar/dist/styles.css';
// ----------------------------------------
// Needed for fontawesome SSR to work properly
import '@fortawesome/fontawesome-svg-core/styles.css';

config.autoAddCss = false;
// ----------------------------------------

const PROTECTED_ROUTES: ProtectedRoutes = [
  { path: '/artist' },
  { path: '/user/settings' },
  { path: '/admin' },
  { path: '/admin/moderate/[userIdOrPublicAddress]', setting: 'onlyAdmin' },
  { path: '/playlist/new' },
  { path: '/dashboard' },
  { path: '/notifications' },

  { path: '/dashboard/profile' },
  { path: '/dashboard/email' },
  { path: '/dashboard/earnings' },
  { path: '/admin/artist-applications', setting: 'soundTeam' },
  { path: '/artist-application', setting: 'onlyNonArtist' },
  //only Artist : Complete Artist Profiles
  { path: '/dashboard/upload/edit/[releaseType]/[draftId]/step/[stepId]', setting: 'onlyArtist' },
  { path: '/dashboard/edit/[releaseId]/[stepId]', setting: 'onlyArtist' },
  {
    path: '/secret/dashboard/upload/[releaseType]/[draftId]/step/[stepId]/preview',
    setting: 'onlyArtist',
  },
  { path: '/dashboard/releases', setting: 'onlyArtist' },
  { path: '/dashboard/upload', setting: 'onlyArtist' },
  { path: '/secret/dashboard/upload', setting: 'onlyArtist' },
  { path: '/dashboard/upload/[draftId]/[stepId]', setting: 'onlyArtist' },
  { path: '/secret/dashboard/upload/[releaseType]/[draftId]/step/[stepId]', setting: 'onlyArtist' },
  { path: '/dashboard/community', setting: 'onlyArtist' },
];

if (isClientSide(globalThis.window)) {
  /** Adds error reporting for all uncaught exceptions */
  global.window.addEventListener('error', event => {
    const message = event.error instanceof Error ? event.error.message : 'Unknown global error';
    logError({
      action: 'global-error',
      error: event.error,
      errorType: 'GLOBAL_ERROR',
      isArtistOnlyPage: null,
      level: 'fatal',
      message,
      pillar: 'GLOBAL',
    });
  });

  /** Adds error reporting for all unhandled promise rejections */
  global.window.addEventListener('unhandledrejection', event => {
    const message =
      event.reason instanceof Error
        ? event.reason.message
        : 'Unknown global unhandled promise rejection';

    logError({
      action: 'global-unhandled-promise',
      error: event.reason,
      errorType: 'GLOBAL_UNHANDLED_PROMISE',
      isArtistOnlyPage: null,
      level: 'fatal',
      message,
      pillar: 'GLOBAL',
    });
  });
}

// @ts-expect-error: Not possible to fix in patched version of nextjs-toploader.
const DynamicNextTopLoader = dynamic(() => import('nextjs-toploader'));

const DynamicProvider = dynamic(() => import('@components/Provider').then(mod => mod.Provider));

const DynamicLayout = dynamic(() => import('@components/layout').then(mod => mod.Layout));

const DynamicGraphQLReactQueryProvider = dynamic(() =>
  import('@graphql/reactQuery/client').then(mod => mod.GraphQLReactQueryProvider),
);

const DynamicPrivateRouteGuard = dynamic(() =>
  import('@components/PrivateRouteGuard').then(v => v.PrivateRouteGuard),
);

// Avoid bloating app.js bundle, dynamically import components where we can
const DynamicDatadogGlobalScope = dynamic(() =>
  import('@components/DatadogGlobalScope').then(mod => mod.DatadogGlobalScope),
);

const DynamicSealedSessionReporter = dynamic(() =>
  import('@store/analytics/playSession').then(mod => mod.SealedSessionReporter),
);

const DynamicSentryGlobalScope = dynamic(() =>
  import('@components/SentryGlobalScope').then(mod => mod.SentryGlobalScope),
);

const DynamicSetupProfile = dynamic(
  () => import('@components/SetupProfile/lazy').then(v => v.SetupProfileBehindAuth),
  {
    ssr: false,
  },
);

function defaultLayout(page: ReactElement, _pageProps?: unknown) {
  return page;
}

export type NextPageWithLayout<P = SharedProps, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function MyApp({ Component, pageProps, ...rest }: AppPropsWithLayout) {
  const getLayout = Component.getLayout || defaultLayout;

  const { asPath, pathname } = useRouter();
  useIgnoreBenignErrors();
  useUpdateParams();
  useTrackPopState();

  // Track initial path for left nav back button
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const pathname = Router.pathname;
    if (!DASHBOARD_PATHNAMES.includes(pathname) && !UPLOAD_PATHNAMES.includes(pathname)) {
      LeftNavState.previousPath = Router.asPath;
    }
  }, []);

  useSyncRouterHistory();

  return (
    <>
      <DynamicNextTopLoader color={theme.colors.brand500.value} />
      <Head>
        <meta
          name="viewport"
          content="maximum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, viewport-fit=cover"
        />
      </Head>

      <Suspense fallback={null}>
        <NavigationEvents />
      </Suspense>

      <DynamicGraphQLReactQueryProvider>
        <DynamicProvider>
          <DynamicSetupProfile />
          <DynamicSentryGlobalScope />
          <DynamicDatadogGlobalScope />
          <DynamicSealedSessionReporter />
          <DynamicPrivateRouteGuard routes={PROTECTED_ROUTES}>
            <DynamicLayout {...pageProps}>
              {/* used in end-to-end tests */}
              <div data-testid={pathname} />
              {getLayout(
                <Component
                  {...rest}
                  {...pageProps}
                  //TODO: https://linear.app/sound/issue/PROD-1485/refactor-the-force-remount-of-dynamic-route-pages
                  /**
                   * Forces dynamic route pages to remount when changing route
                   *
                   * NextJS doesnt remount dynamic route pages when changing route by default so apps with state heavy UI
                   * that needs to persist when changing between routes can maintain high performance
                   */
                  key={asPath.split('?')[0]}
                />,
                pageProps,
              )}
            </DynamicLayout>
          </DynamicPrivateRouteGuard>
        </DynamicProvider>
      </DynamicGraphQLReactQueryProvider>
    </>
  );
}
